import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useUnitViewFormat } from './UnitViewFormatProvider'
import { UNIT_VIEW_PAPER_LAYOUT, EVENTS_GRAFANA } from 'utils/constants/constants'
import {
  getInitialPageId,
  getPaperPagesWithRightHost,
  getPaperPagesWithRightVisibleSection,
} from 'utils/paperBookUtils'
import grafanaUtils from 'utils/grafana/grafanaUtils'

// TODO eliminar estados no relevantes y usar este initialState en inicialización/destroy de los useState
const initialState = {
  currentVisiblePages: [],
  errorPageIds: [],
  goToNextPage: () => {},
  goToPageById: () => {},
  goToPageByNumber: () => {},
  goToPreviousPage: () => {},
  handleActivePageChange: () => {},
  handleError: () => {},
  handleUnitLoaded: () => {},
  handleZoomChange: () => {},
  isOpenPaperViewSidebar: false,
  isReady: false,
  setInstance: () => {},
  setIsOpenPaperViewSidebar: () => {},
  setZoom: () => {},
  zoom: 1,
}

export const MIN_ZOOM = 1
export const MAX_ZOOM = 5
export const DEFAULT_ZOOM = 1
export const PAPER_SDK_CONTAINER_ID = 'paper-view-sdk-container'

const PaperViewContext = createContext(initialState)

export const PaperViewProvider = ({ children }) => {
  const [instance, setInstance] = useState(undefined)
  const [internalZoom, setInternalZoom] = useState(DEFAULT_ZOOM)
  const [currentPageId, setCurrentPageId] = useState(undefined)
  const [visiblePageIds, setVisiblePageIds] = useState([])
  const [isReadyUnit, setIsReadyUnit] = useState(false)
  const [isOpenPaperViewSidebar, setIsOpenPaperViewSidebar] = useState(false)
  const history = useHistory()

  // When navigating to a specific section, we first navigate to its page.
  // Since a page can contain multiple sections, we use this ref to temporarily
  // store the target section's ID. This ensures that once the page is loaded,
  // the correct section is set, instead of defaulting to the first section on the page.
  const temporalSectionIdRef = useRef(null)
  const [currentSectionId, setCurrentSectionId] = useState(undefined)

  const { handleLoadingComplete } = useUnitViewFormat()

  const rawUnitPaperPages = useSelector((state) => state.navV2?.unit?.pages || [])
  const unitPaperSections = useSelector((state) => state.navV2?.unit?.sections || [])

  const mappedUnitPaperPages = useMemo(() => {
    if (!rawUnitPaperPages?.length || !unitPaperSections?.length) return []

    const paperPagesWithRightVisibleSection = getPaperPagesWithRightVisibleSection(
      rawUnitPaperPages,
      unitPaperSections
    )

    const paperPagesWithRightHost = getPaperPagesWithRightHost(
      paperPagesWithRightVisibleSection,
      window.viewer.contentServer
    )

    const paperPagesWithTitle = paperPagesWithRightHost.map((page) => ({
      ...page,
      title: `Paper page number: ${page.number}`,
    }))

    const paperPagesWithIndex = paperPagesWithTitle.map((page, index) => ({
      ...page,
      index,
    }))

    return paperPagesWithIndex
  }, [rawUnitPaperPages, unitPaperSections])

  useEffect(() => {
    if (!instance) return
    setCurrentPageId(instance.getCurrentPageId())
    setVisiblePageIds(instance.getVisiblePageIds())
  }, [instance])

  const setZoom = useCallback(
    (newZoomValue) => {
      if (!instance) return

      instance.setZoom(newZoomValue)
    },
    [instance]
  )

  const handleZoomChange = useCallback(
    (zoomFromSDK) => {
      setInternalZoom(zoomFromSDK)
    },
    [setInternalZoom]
  )

  const handleError = useCallback((error) => {
    // TODO
    console.error('PaperView Error:', error?.toString ? error.toString() : error)
  }, [])

  const handleActivePageChange = useCallback(
    (currentPageId, visiblePageIds) => {
      if (!temporalSectionIdRef.current) {
        setCurrentSectionId(
          mappedUnitPaperPages?.find((page) => page.id === currentPageId)?.sectionId
        )
      } else {
        setCurrentSectionId(temporalSectionIdRef.current)
        temporalSectionIdRef.current = null
      }
      setCurrentPageId(currentPageId)
      setVisiblePageIds(visiblePageIds)
    },
    [setCurrentPageId, setVisiblePageIds, setCurrentSectionId, mappedUnitPaperPages]
  )

  const handleUnitLoaded = useCallback(() => {
    handleLoadingComplete(true)
    setIsReadyUnit(true)
  }, [handleLoadingComplete])

  const goToPageBySectionId = useCallback(
    (sectionId, sectionIds) => {
      if (!instance || !mappedUnitPaperPages?.length) return
      const firstPageId = getInitialPageId(sectionId, mappedUnitPaperPages, sectionIds)

      if (firstPageId === currentPageId) {
        setCurrentSectionId(sectionId)
        return
      }

      temporalSectionIdRef.current = sectionId
      instance.setCurrentPageId(firstPageId)
      grafanaUtils.pushEvent(EVENTS_GRAFANA.click_stepper_navigation)
    },
    [instance, mappedUnitPaperPages, currentPageId]
  )

  const goToPageByNumber = useCallback(
    (pageNumber) => {
      if (!instance || !mappedUnitPaperPages?.length) return

      const pageFound = mappedUnitPaperPages.find((page) => page.number === pageNumber)

      const pageFoundId = pageFound.id

      instance.setCurrentPageId(pageFoundId)
    },
    [instance, mappedUnitPaperPages]
  )

  const goToPageById = useCallback(
    (id) => {
      if (!instance) return
      return instance.setCurrentPageId(id)
    },
    [instance]
  )

  const goToPreviousPage = useCallback(() => {
    if (!instance || !mappedUnitPaperPages?.length) return
    let previousPageIndex

    const firstPageVisibleId = visiblePageIds[0]
    const indexOfFirstVisiblePage = mappedUnitPaperPages.findIndex(
      (page) => page.id === firstPageVisibleId
    )

    if (instance.getPageLayout() === UNIT_VIEW_PAPER_LAYOUT.single) {
      previousPageIndex = indexOfFirstVisiblePage - 1
    } else {
      previousPageIndex = indexOfFirstVisiblePage - 2
      if (previousPageIndex < 0) {
        previousPageIndex = indexOfFirstVisiblePage - 1
      }
    }

    if (previousPageIndex >= 0) {
      const previousPage = mappedUnitPaperPages[previousPageIndex]
      instance.setCurrentPageId(previousPage.id)
    }
    grafanaUtils.pushEvent(EVENTS_GRAFANA.click_previous_page)
  }, [instance, mappedUnitPaperPages, visiblePageIds])

  const getNextPage = useCallback(() => {
    if (!instance || !mappedUnitPaperPages?.length) return null

    const lastPageVisibleId = visiblePageIds[visiblePageIds.length - 1]
    const indexOfLastVisiblePage = mappedUnitPaperPages.findIndex(
      (page) => page.id === lastPageVisibleId
    )

    const nextPageIndex = indexOfLastVisiblePage + 1
    if (nextPageIndex < mappedUnitPaperPages.length) {
      return mappedUnitPaperPages[nextPageIndex]
    }

    return null
  }, [instance, mappedUnitPaperPages, visiblePageIds])

  const goToNextPage = useCallback(() => {
    const nextPage = getNextPage()
    if (nextPage) {
      instance.setCurrentPageId(nextPage.id)
    }

    grafanaUtils.pushEvent(EVENTS_GRAFANA.click_next_page)
  }, [getNextPage, instance])

  const currentVisiblePages = useMemo(() => {
    if (!instance) return
    return mappedUnitPaperPages.filter((page) => visiblePageIds.includes(page.id))
  }, [instance, mappedUnitPaperPages, visiblePageIds])

  const reset = useCallback(() => {
    setInstance(undefined)
    setIsReadyUnit(false)
    setCurrentPageId(undefined)
    setVisiblePageIds([])
    setCurrentSectionId(undefined)
    setInternalZoom(DEFAULT_ZOOM)
    temporalSectionIdRef.current = null
  }, [])

  useEffect(() => {
    if (!instance) return

    const handleKeyDown = (event) => {
      if (
        event.target &&
        event.target.tagName === 'INPUT' &&
        event.target.type !== 'checkbox' &&
        event.target.type !== 'radio'
      ) {
        return
      }

      history.action = 'PUSH'
      if (event.key === 'ArrowLeft') {
        goToPreviousPage()
      } else if (event.key === 'ArrowRight') {
        goToNextPage()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [instance, goToPreviousPage, goToNextPage, history])

  const contextValue = useMemo(
    () => ({
      currentPageId,
      currentSectionId,
      currentVisiblePages,
      getNextPage,
      goToNextPage,
      goToPageById,
      goToPageByNumber,
      goToPageBySectionId,
      goToPreviousPage,
      handleActivePageChange,
      handleError,
      handleUnitLoaded,
      handleZoomChange,
      isOpenPaperViewSidebar,
      isReady: !!instance && isReadyUnit,
      reset,
      setInstance,
      setIsOpenPaperViewSidebar,
      setZoom,
      unitPaperPages: mappedUnitPaperPages,
      zoom: internalZoom,
    }),
    [
      currentPageId,
      currentSectionId,
      currentVisiblePages,
      reset,
      getNextPage,
      goToNextPage,
      goToPageById,
      goToPreviousPage,
      goToPageByNumber,
      goToPageBySectionId,
      handleActivePageChange,
      handleError,
      handleUnitLoaded,
      handleZoomChange,
      isOpenPaperViewSidebar,
      isReadyUnit,
      instance,
      setInstance,
      setIsOpenPaperViewSidebar,
      setZoom,
      mappedUnitPaperPages,
      internalZoom,
    ]
  )

  return <PaperViewContext.Provider value={contextValue}>{children}</PaperViewContext.Provider>
}

export const usePaperView = () => {
  const context = useContext(PaperViewContext)
  if (!context) {
    throw new Error('usePaperView must be used within a PaperViewProvider')
  }
  return context
}
